/* eslint-disable indent */
import { IconBuildingStore, IconBuildingFactory2 } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';
import { InfoTip } from '@/components/Help/InfoTip';
import { RichSelectOption } from '@/components/Controls/RichSelect';

/**
 * Usage of a structure (group, company, branch):
 * - DISTRIBUTOR: resells / distributes the Lucius solution to other groups
 * - OPERATOR: operates its own vehicles (inspections are carried out for it)
 * - BOTH: distributor and operator at the same time
 */
export enum UsageType {
  DISTRIBUTOR = 'DISTRIBUTOR',
  OPERATOR = 'OPERATOR',
  BOTH = 'BOTH',
}

/** Values used by the "usage" filter (see `getOptionsTypeFilter`). */
export const USAGE_FILTER_VALUE: Record<UsageType, string> = {
  [UsageType.DISTRIBUTOR]: '1',
  [UsageType.OPERATOR]: '2',
  [UsageType.BOTH]: '3',
};

export const getUsageType = (
  isDistributor?: boolean,
  isBranch?: boolean,
): UsageType | null => {
  if (isDistributor && isBranch) return UsageType.BOTH;
  if (isDistributor) return UsageType.DISTRIBUTOR;
  if (isBranch) return UsageType.OPERATOR;
  return null;
};

export const getUsageLabelKey = (usage: UsageType) => {
  switch (usage) {
    case UsageType.DISTRIBUTOR:
      return 'common:distributor';
    case UsageType.OPERATOR:
      return 'common:operator';
    case UsageType.BOTH:
      return 'common:distributorAndBranch';
  }
};

const usageStyles: Record<UsageType, string> = {
  [UsageType.DISTRIBUTOR]:
    'bg-sky-50 text-sky-600 border-sky-200 dark-mode:bg-sky-500/10 dark-mode:border-sky-500/30',
  [UsageType.OPERATOR]:
    'bg-emerald-50 text-emerald-600 border-emerald-200 dark-mode:bg-emerald-500/10 dark-mode:border-emerald-500/30',
  [UsageType.BOTH]:
    'bg-gradient-to-br from-sky-50 to-emerald-50 text-violet-600 border-violet-200 dark-mode:from-sky-500/10 dark-mode:to-emerald-500/10 dark-mode:border-violet-500/30',
};

interface UsageIconProps {
  usage: UsageType;
  size?: 'sm' | 'md';
  withTooltip?: boolean;
  className?: string;
}

export const UsageIcon = ({
  usage,
  size = 'md',
  withTooltip = true,
  className,
}: UsageIconProps) => {
  const { t } = useTranslation();
  const label = t(getUsageLabelKey(usage));
  const iconSize = size === 'sm' ? 13 : 16;
  const boxSize =
    size === 'sm' ? 'h-[22px] min-w-[22px]' : 'h-[28px] min-w-[28px]';

  return (
    <span
      title={withTooltip ? label : undefined}
      aria-label={label}
      role="img"
      className={twMerge(
        'inline-flex items-center justify-center gap-0.5 px-1 rounded-full border',
        boxSize,
        usageStyles[usage],
        className,
      )}
    >
      {usage === UsageType.BOTH ? (
        <>
          <IconBuildingStore
            size={iconSize - 1}
            stroke={1.9}
            className="text-sky-600"
          />
          <IconBuildingFactory2
            size={iconSize - 1}
            stroke={1.9}
            className="text-emerald-600"
          />
        </>
      ) : usage === UsageType.DISTRIBUTOR ? (
        <IconBuildingStore size={iconSize} stroke={1.9} />
      ) : (
        <IconBuildingFactory2 size={iconSize} stroke={1.9} />
      )}
    </span>
  );
};

interface UsageBadgeProps {
  /** DISTRIBUTOR or OPERATOR ("both" is displayed as two badges). */
  usage: UsageType.DISTRIBUTOR | UsageType.OPERATOR;
  className?: string;
}

/** Usage pictogram followed by its label (same display as `RoleBadge`). */
export const UsageBadge = ({ usage, className }: UsageBadgeProps) => {
  const { t } = useTranslation();

  return (
    <span className={twMerge('inline-flex items-center gap-1.5', className)}>
      <UsageIcon usage={usage} size="sm" withTooltip={false} />
      <span>
        {t(
          usage === UsageType.DISTRIBUTOR
            ? 'common:distributor'
            : 'common:branch',
        )}
      </span>
    </span>
  );
};

interface UsageCellProps {
  isDistributor?: boolean;
  isBranch?: boolean;
}

/** Usages of a structure in a table cell: one badge per usage, stacked. */
export const UsageCell = ({ isDistributor, isBranch }: UsageCellProps) =>
  isDistributor || isBranch ? (
    <div className="flex justify-center">
      <div className="inline-flex flex-col items-start gap-1.5">
        {isDistributor && (
          <UsageBadge usage={UsageType.DISTRIBUTOR} className="text-xs" />
        )}
        {isBranch && (
          <UsageBadge usage={UsageType.OPERATOR} className="text-xs" />
        )}
      </div>
    </div>
  ) : null;

interface UsageLegendProps {
  className?: string;
}

/** Legend explaining the usage icons, displayed above tables. */
export const UsageLegend = ({ className }: UsageLegendProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={twMerge(
        'flex flex-wrap items-center gap-x-3.5 gap-y-1.5 text-xs text-gray-600',
        className,
      )}
    >
      <span className="flex items-center gap-1 font-medium text-gray-700">
        {t('common:usage')}
        <InfoTip
          title={t('common:usage')}
          text={t('common:usageInfoTip')}
          docSlug="usages-distributeur-exploitant"
        />
      </span>
      {Object.values(UsageType).map((usage) => (
        <span key={usage} className="flex items-center gap-1.5">
          <UsageIcon usage={usage} size="sm" withTooltip={false} />
          {t(getUsageLabelKey(usage))}
        </span>
      ))}
    </div>
  );
};

/** Options of the "usage" filter (same labels as the usage column tags). */
export const getUsageFilterOptions = (
  t: (key: string) => string,
): RichSelectOption[] =>
  [
    { usage: UsageType.DISTRIBUTOR, labelKey: 'common:distributor' },
    { usage: UsageType.OPERATOR, labelKey: 'common:branch' },
    { usage: UsageType.BOTH, labelKey: 'common:distributorAndBranch' },
  ].map(({ usage, labelKey }) => ({
    label: t(labelKey),
    value: USAGE_FILTER_VALUE[usage],
    // Fixed width so that the labels are aligned (the "both" icon is wider)
    icon: (
      <span className="flex w-9">
        <UsageIcon usage={usage} size="sm" withTooltip={false} />
      </span>
    ),
    iconInMenuOnly: true,
  }));
